import { Controller } from "@hotwired/stimulus"
import { yPositionsPatterns, yPositionsPatternsMini, IMAGE_BASE_WIDTH, LINE_GAP, FONT_SIZE_PRINT, FONT_SIZE_MINI, type PositionPattern } from "../constants/menu_position_patterns"

type TemplateId = "1" | "2" | "3" | "4" | "5"

export default class extends Controller {
  static targets = ["downloadBtn", "resultImage", "loading"]
  static values = {
    templateId: String,
    autoGenerate: Boolean,
    autoPrint: Boolean,
    defaultPl: Number,
    defaultPt: Number,
    mini: Boolean,
  }

  declare downloadBtnTarget: HTMLButtonElement
  declare hasDownloadBtnTarget: boolean
  declare resultImageTarget: HTMLImageElement
  declare hasResultImageTarget: boolean
  declare loadingTarget: HTMLElement
  declare hasLoadingTarget: boolean

  declare templateIdValue: TemplateId
  declare hasTemplateIdValue: boolean
  declare autoGenerateValue: boolean
  declare hasAutoGenerateValue: boolean
  declare autoPrintValue: boolean
  declare hasAutoPrintValue: boolean
  declare defaultPlValue: number
  declare hasDefaultPlValue: boolean
  declare defaultPtValue: number
  declare hasDefaultPtValue: boolean
  declare miniValue: boolean
  declare hasMiniValue: boolean

  connect() {
    this.setupDownloadButton()
    this.applyPaddingFromParams()
    this.autoGenerateIfNeeded()
  }

  private async setupCanvas(): Promise<HTMLCanvasElement> {
    // フォントが読み込まれるまで待機
    await this.waitForFont()

    const { img, width, height } = await this.loadTemplateImage()

    const canvas = document.createElement("canvas")
    canvas.style.display = "none"
    canvas.width = width
    canvas.height = height

    const ctx = canvas.getContext("2d")
    if (!ctx) throw new Error("Canvas context could not be created")

    ctx.drawImage(img, 0, 0, width, height)
    this.drawMenuItems(ctx)

    return canvas
  }

  private async waitForFont(): Promise<void> {
    const fontFamily = "Zen Kaku Gothic New"
    const fontWeight = "normal"
    const fontSpec = `${fontWeight} 1em "${fontFamily}"`

    // フォントが既に読み込まれているか確認
    if (document.fonts.check(fontSpec)) {
      return
    }

    // フォントを明示的に読み込む
    try {
      await document.fonts.load(fontSpec)
    } catch (error) {
      console.warn("Failed to load font:", fontFamily, error)
    }

    // フォントが読み込まれるまで待機（最大10秒）
    const maxWaitTime = 10000 // 10秒
    const checkInterval = 100 // 100msごとに確認
    const startTime = Date.now()

    while (!document.fonts.check(fontSpec)) {
      if (Date.now() - startTime > maxWaitTime) {
        console.warn(`Font "${fontFamily}" did not load within ${maxWaitTime}ms`)
        break
      }
      await new Promise((resolve) => setTimeout(resolve, checkInterval))
    }
  }

  private async loadTemplateImage(): Promise<{ img: HTMLImageElement; width: number; height: number }> {
    const preview = this.getPreviewElements()[0] || this.element

    const imgElement = preview.querySelector("img") as HTMLImageElement | null
    if (imgElement && imgElement.src) {
      return await new Promise((resolve, reject) => {
        const complete = imgElement.complete && imgElement.naturalWidth > 0
        if (complete) {
          resolve({ img: imgElement, width: imgElement.naturalWidth, height: imgElement.naturalHeight })
          return
        }

        const img = new Image()
        img.crossOrigin = "anonymous"
        img.onload = () => resolve({ img, width: img.naturalWidth, height: img.naturalHeight })
        img.onerror = () => reject(new Error("Failed to load IMG template"))
        img.src = imgElement.src
      })
    }

    throw new Error("Template not found (IMG)")
  }

  private getPreviewElements(): HTMLElement[] {
    const selectors = [
      ".c-menu-preview",
    ]

    const scoped = selectors.flatMap((selector) =>
      Array.from(this.element.querySelectorAll(selector))
    ) as HTMLElement[]

    if (scoped.length > 0) {
      return scoped
    }

    return Array.from(document.querySelectorAll(selectors.join(", "))) as HTMLElement[]
  }

  private drawMenuItems(ctx: CanvasRenderingContext2D) {
    // 高解像度画像対応: Canvas幅からスケール係数を算出
    const scale = ctx.canvas.width / IMAGE_BASE_WIDTH

    ctx.textAlign = "left"
    ctx.textBaseline = "middle"

    const menuItemElements = Array.from(
      this.element.querySelectorAll('.c-menu-preview__menu-item')
    ).slice(0, 5) as HTMLElement[]

    // templateIdからパターンを取得
    const templateId = (this.hasTemplateIdValue ? this.templateIdValue : "1") as TemplateId
    const isMini = this.hasMiniValue && this.miniValue
    const pattern = this.getPatternForTemplate(templateId, isMini);
    const fontSize = (isMini ? FONT_SIZE_MINI : FONT_SIZE_PRINT) * scale

    menuItemElements.forEach((menuItemElement, index) => {
      if (!menuItemElement) return

      const computedStyle = window.getComputedStyle(menuItemElement)

      const textColor = pattern.textColor || computedStyle.color || "#333333";

      // パターンから位置情報を取得し、スケールを適用
      const yPosition = pattern.positions[index] * scale
      const x = (pattern.x ?? 0) * scale
      const y = yPosition ?? 0
      const letterSpacing = pattern.letterSpacing ?? 0
      const fontWeight = (computedStyle.getPropertyValue("font-weight") || "normal").trim()
      const fontFamily = (computedStyle.getPropertyValue("font-family") || "sans-serif").trim()

      const totalHeight = (pattern.height ?? 88) * scale
      const bgHeight = totalHeight
      const yCenter = y + bgHeight / 2

      ctx.fillStyle = textColor
      ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`
      ctx.letterSpacing = `${letterSpacing}em`
      const lines = String(menuItemElement.textContent).split("\n", 2)
      const lineOffsetBase = fontSize + LINE_GAP * scale
      const centerOffset = ((lines.length - 1) / 2) * lineOffsetBase
      lines.forEach((line, li) => {
        const offset = (li * lineOffsetBase) - centerOffset
        ctx.fillText(line, x, yCenter + offset)
      })
    })
  }

  private getPatternForTemplate(templateId: TemplateId, isMini: boolean = false): PositionPattern {
    const patterns = isMini ? yPositionsPatternsMini : yPositionsPatterns
    // パターンマップを検索して、templateIdが含まれるパターンを返す
    for (const pattern of Object.values(patterns)) {
      if (pattern.ids.includes(templateId)) {
        return pattern
      }
    }
    // フォールバック: デフォルトパターンを返す
    return patterns["default"]
  }

  private applyPaddingFromParams() {
    if (!this.hasResultImageTarget) return

    const params = new URLSearchParams(window.location.search)

    this.applyPaddingValue(params, "pl", "padding-left", this.hasDefaultPlValue ? this.defaultPlValue : undefined)
    this.applyPaddingValue(params, "pt", "padding-top", this.hasDefaultPtValue ? this.defaultPtValue : undefined)
  }

  private applyPaddingValue(params: URLSearchParams, key: string, property: string, defaultValue?: number) {
    const rawValue = params.get(key)

    let value: number | undefined
    if (rawValue !== null) {
      const parsed = Number.parseFloat(rawValue)
      if (Number.isFinite(parsed)) value = parsed
    } else if (defaultValue !== undefined) {
      value = defaultValue
    }

    if (value === undefined) return

    this.resultImageTarget.style.setProperty(property, `${value}mm`)
  }

  private setupDownloadButton() {
    if (!this.hasDownloadBtnTarget) return
    this.downloadBtnTarget.addEventListener("click", () => {
      this.downloadAsPNG()
    })
  }

  private async autoGenerateIfNeeded() {
    if (!this.hasAutoGenerateValue || !this.autoGenerateValue) return

    try {
      await this.generateImageForDisplay()
    } catch (error) {
      console.error("Failed to auto-generate menu image:", error)
      alert("メニュー画像の生成に失敗しました。ページを再読み込みしてください。")
    }
  }

  private async generateImageForDisplay() {
    this.setLoading(true)
    const canvas = await this.setupCanvas()
    const dataUrl = canvas.toDataURL("image/png")

    if (this.hasResultImageTarget) {
      this.resultImageTarget.src = dataUrl
      this.resultImageTarget.classList.add("is-loaded")

      // 画像が実際に描画されるのを待つ
      await this.waitForImageRender()
    }

    this.setLoading(false)

    // 自動印刷が有効な場合、印刷ダイアログを表示
    if (this.hasAutoPrintValue && this.autoPrintValue) {
      window.print()
    }
  }

  private waitForImageRender(): Promise<void> {
    return new Promise((resolve) => {
      // requestAnimationFrameを2回呼び出して、描画が完了するのを確実に待つ
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          resolve()
        })
      })
    })
  }

  private async downloadAsPNG() {
    try {
      const canvas = await this.setupCanvas()
      const link = document.createElement("a")
      link.download = "menu.png"
      link.href = canvas.toDataURL("image/png")
      link.click()
    } catch (error) {
      console.error("Failed to generate PNG:", error)
      alert("PNG画像の生成に失敗しました")
    }
  }

  private setLoading(isLoading: boolean) {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.toggle("is-hidden", !isLoading)
    }

    if (this.hasResultImageTarget) {
      this.resultImageTarget.classList.toggle("is-hidden", isLoading)
    }
  }
}
